import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { fetchGalleryById, accessGalleryByPassword, getGalleryImageDownloadFileUrl, createCheckoutSession } from '../services/api';
import { Gallery as GalleryType, PrintOption } from '../types';
import { buildGalleryResponsiveSrcSet, getGalleryDisplayImageUrl, preloadImage } from '../utils/images';
import { buildGalleryPath, slugify } from '../utils/slug';

type RouteParams = {
    id: string;
    slug?: string;
};

type GalleryPageProps = {
    setCurrentGalleryTitle?: (title: string | null) => void;
};

type InfoPanelState =
    | { type: 'gallery' }
    | { type: 'image'; imageId: string }
    | null;

type CartItem = {
    key: string;
    imageId: string;
    imageTitle: string;
    thumbnailUrl?: string;
    printOptionId: number;
    printLabel: string;
    priceCents: number;
    currency: string;
    quantity: number;
};

const GalleryInfoIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" className="gallery-info-icon" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="8" r="1.2" fill="currentColor" />
        <path d="M12 11.2v5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const GalleryDownloadIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" className="gallery-info-icon" aria-hidden="true" focusable="false">
        <path d="M12 4v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8.4 10.8L12 14.4l3.6-3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 18h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const GalleryCartIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" className="gallery-info-icon" aria-hidden="true" focusable="false">
        <path d="M6.4 7.2h13l-1.5 7.1H8L6.4 7.2Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M6.4 7.2 5.8 4.8H3.9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="9" cy="18.2" r="1" fill="currentColor" />
        <circle cx="16.8" cy="18.2" r="1" fill="currentColor" />
    </svg>
);

const Gallery: React.FC<GalleryPageProps> = ({ setCurrentGalleryTitle }) => {
    const { id, slug } = useParams<RouteParams>();
    const history = useHistory();
    const location = useLocation();
    const galleryId = id;
    const cartStorageKey = `galleryCart:${galleryId}`;
    const [gallery, setGallery] = useState<GalleryType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [requiresPassword, setRequiresPassword] = useState<boolean>(false);
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'carousel'>('grid');
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [infoPanelState, setInfoPanelState] = useState<InfoPanelState>(null);
    const [expandedDreamy, setExpandedDreamy] = useState<Record<string, boolean>>({});
    const [purchaseImageId, setPurchaseImageId] = useState<string | null>(null);
    const [selectedPrintOptionId, setSelectedPrintOptionId] = useState<number | null>(null);
    const [purchasing, setPurchasing] = useState(false);
    const [purchaseError, setPurchaseError] = useState<string | null>(null);
    const [cartItems, setCartItems] = useState<CartItem[]>(() => {
        try {
            const storedCart = sessionStorage.getItem(cartStorageKey);
            return storedCart ? JSON.parse(storedCart) : [];
        } catch {
            return [];
        }
    });
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [cartNotice, setCartNotice] = useState<string | null>(null);

    const updateViewInUrl = (
        nextView: 'grid' | 'list' | 'carousel',
        options?: { imageIndex?: number; push?: boolean }
    ) => {
        const params = new URLSearchParams(location.search);

        params.set('view', nextView);

        if (nextView === 'carousel' && typeof options?.imageIndex === 'number') {
            params.set('image', String(options.imageIndex));
        } else {
            params.delete('image');
        }

        const nextSearch = params.toString();
        const nextLocation = {
            pathname: location.pathname,
            search: nextSearch ? `?${nextSearch}` : '',
        };

        if (options?.push) {
            history.push(nextLocation);
            return;
        }

        history.replace(nextLocation);
    };

    useEffect(() => {
        const storedToken = sessionStorage.getItem(`galleryAccessToken:${galleryId}`);
        setAccessToken(storedToken || null);
    }, [galleryId]);

    useEffect(() => {
        try {
            sessionStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
        } catch {
            // Ignore storage failures; checkout still works during the current render.
        }
    }, [cartItems, cartStorageKey]);

    useEffect(() => {
        if (!gallery) return;
        const expectedSlug = slugify(gallery.title);
        if (!expectedSlug) return;
        if (slug !== expectedSlug) {
            history.replace(buildGalleryPath({ id: gallery.id, title: gallery.title }));
        }
    }, [gallery, history, slug]);

    useEffect(() => {
        if (!setCurrentGalleryTitle) return undefined;

        setCurrentGalleryTitle(gallery?.title || null);

        return () => {
            setCurrentGalleryTitle(null);
        };
    }, [gallery?.title, setCurrentGalleryTitle]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const getGallery = async () => {
            const tokenForRequest = sessionStorage.getItem(`galleryAccessToken:${galleryId}`) || accessToken;
            try {
                const data = await fetchGalleryById(galleryId, tokenForRequest);
                setGallery(data);
                setRequiresPassword(false);
            } catch (err) {
                // @ts-ignore
                const status = err && err.response && err.response.status;
                // @ts-ignore
                const responseData = err && err.response && err.response.data;
                const requiresAccess =
                    status === 401 ||
                    status === 403 ||
                    Boolean(responseData && responseData.requiresPassword) ||
                    (responseData && responseData.message === 'Gallery is private');

                if (requiresAccess) {
                    setRequiresPassword(true);
                    setError('Indtast adgangskode');
                    if (tokenForRequest) {
                        sessionStorage.removeItem(`galleryAccessToken:${galleryId}`);
                        setAccessToken(null);
                    }
                } else if (status === 404) {
                    setError('Galleri ikke fundet');
                } else {
                    setError('Kunne ikke hente galleriet');
                }
            } finally {
                setLoading(false);
            }
        };

        getGallery();
    }, [galleryId, accessToken]);

    const images = useMemo(() => gallery?.images || [], [gallery?.images]);
    const orderedGalleryBlocks = useMemo(() => {
        return [...(gallery?.blocks || [])]
            .filter((block) => block.isVisible !== false)
            .sort((a, b) => {
                const orderDiff = Number(a.sortOrder) - Number(b.sortOrder);
                if (orderDiff !== 0) return orderDiff;
                return Number(a.id) - Number(b.id);
            });
    }, [gallery?.blocks]);
    const activeImage = images[activeIndex];
    const activeInfoPanelImageId = infoPanelState?.type === 'image' ? infoPanelState.imageId : null;
    const infoPanelImage = useMemo(() => {
        if (!infoPanelState || infoPanelState.type !== 'image') return null;
        return images.find((image) => image.id === infoPanelState.imageId) || null;
    }, [images, infoPanelState]);
    const canDownload = Boolean(gallery?.isPasswordProtected);
    const purchaseImage = useMemo(() => {
        if (!purchaseImageId) return null;
        return images.find((image) => String(image.id) === String(purchaseImageId)) || null;
    }, [images, purchaseImageId]);

    const getActivePrintOptions = (image?: GalleryType['images'][number] | null) => {
        return [...(image?.printOptions || [])]
            .filter((option) => option.isActive !== false)
            .sort((a, b) => {
                const orderDiff = Number(a.sortOrder) - Number(b.sortOrder);
                if (orderDiff !== 0) return orderDiff;
                return Number(a.id || 0) - Number(b.id || 0);
            });
    };

    const formatPrintPrice = (option: PrintOption) => {
        const formatter = new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency: option.currency || 'dkk',
            minimumFractionDigits: option.priceCents % 100 === 0 ? 0 : 2,
        });
        return formatter.format(option.priceCents / 100);
    };

    const formatCartAmount = (priceCents: number, currency = 'dkk') => {
        const formatter = new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency,
            minimumFractionDigits: priceCents % 100 === 0 ? 0 : 2,
        });
        return formatter.format(priceCents / 100);
    };

    const cartItemCount = useMemo(() => {
        return cartItems.reduce((sum, item) => sum + item.quantity, 0);
    }, [cartItems]);

    const cartTotalCents = useMemo(() => {
        return cartItems.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
    }, [cartItems]);

    const cartCurrency = cartItems[0]?.currency || 'dkk';

    const getDreamyText = (image?: { aiTexts?: { da?: { dreamy?: string } } }) => {
        return image?.aiTexts?.da?.dreamy || '';
    };

    const getDreamyPreview = (text: string) => {
        if (!text) return '';
        const firstLine = text.split('\n').find((line) => line.trim().length > 0) || text;
        const trimmed = firstLine.trim();
        if (trimmed.length <= 120) return trimmed;
        return `${trimmed.slice(0, 120).trim()}...`;
    };

    const shouldShowDreamyToggle = (text: string) => {
        if (!text) return false;
        const preview = getDreamyPreview(text);
        return preview.length < text.trim().length;
    };

    const handleDownload = (imageId: string) => {
        const url = getGalleryImageDownloadFileUrl(galleryId, imageId, accessToken);
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const closePurchaseDialog = () => {
        if (purchasing) return;
        setPurchaseImageId(null);
        setSelectedPrintOptionId(null);
        setPurchaseError(null);
    };

    const addToCart = (image: GalleryType['images'][number], printOption: PrintOption) => {
        if (!printOption.id) return;
        const key = `${image.id}:${printOption.id}`;
        setCartItems((current) => {
            const existing = current.find((item) => item.key === key);
            if (existing) {
                return current.map((item) =>
                    item.key === key ? { ...item, quantity: Math.min(20, item.quantity + 1) } : item
                );
            }

            return [
                ...current,
                {
                    key,
                    imageId: String(image.id),
                    imageTitle: image.title || 'Uden titel',
                    thumbnailUrl: image.thumbnailUrl || image.url,
                    printOptionId: Number(printOption.id),
                    printLabel: printOption.label,
                    priceCents: printOption.priceCents,
                    currency: printOption.currency || 'dkk',
                    quantity: 1,
                },
            ];
        });
        setCartNotice(`${image.title || 'Print'} er tilføjet til kurven.`);
        window.setTimeout(() => setCartNotice(null), 1800);
        setIsCartOpen(true);
        closePurchaseDialog();
    };

    const updateCartQuantity = (key: string, quantity: number) => {
        setCartItems((current) =>
            current
                .map((item) => (item.key === key ? { ...item, quantity: Math.max(1, Math.min(20, quantity)) } : item))
                .filter((item) => item.quantity > 0)
        );
    };

    const removeCartItem = (key: string) => {
        setCartItems((current) => current.filter((item) => item.key !== key));
    };

    const checkoutCart = async () => {
        if (!cartItems.length) return;
        setPurchasing(true);
        setPurchaseError(null);
        try {
            const data = await createCheckoutSession({
                items: cartItems.map((item) => ({
                    imageId: item.imageId,
                    printOptionId: item.printOptionId,
                    quantity: item.quantity,
                })),
                accessToken,
            });

            if (data?.url) {
                window.location.href = data.url;
                return;
            }

            setPurchaseError('Kunne ikke starte betaling.');
        } catch (e) {
            setPurchaseError('Kunne ikke starte betaling. Prøv igen om lidt.');
        } finally {
            setPurchasing(false);
        }
    };

    const handleBuyPrint = (image: GalleryType['images'][number]) => {
        const options = getActivePrintOptions(image);
        if (!options.length) return;

        if (options.length === 1) {
            addToCart(image, options[0]);
            return;
        }

        setPurchaseError(null);
        setPurchaseImageId(image.id);
        setSelectedPrintOptionId(options[0].id || null);
    };

    const confirmSelectedPurchase = () => {
        if (!purchaseImage) return;
        const selectedOption = getActivePrintOptions(purchaseImage).find((option) => option.id === selectedPrintOptionId);
        if (!selectedOption) {
            setPurchaseError('Vælg en printstørrelse.');
            return;
        }

        addToCart(purchaseImage, selectedOption);
    };

    const hasImageDetails = (image?: GalleryType['images'][number]) => {
        return Boolean(image && (image.description || getDreamyText(image) || canDownload));
    };

    const markGalleryInfoSeen = () => {
        sessionStorage.setItem(`galleryInfoSeen:${galleryId}`, 'true');
    };

    const toggleInfoPanel = (imageId: string) => {
        markGalleryInfoSeen();
        setInfoPanelState((prev) => {
            if (prev?.type === 'image' && prev.imageId === imageId) {
                return null;
            }

            return { type: 'image', imageId };
        });
    };

    const toggleGalleryInfoPanel = () => {
        markGalleryInfoSeen();
        setInfoPanelState((prev) => (prev?.type === 'gallery' ? null : { type: 'gallery' }));
    };

    const closeInfoPanel = () => {
        markGalleryInfoSeen();
        setInfoPanelState(null);
    };

    const handleInfoPanelCloseInteraction = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();
        closeInfoPanel();
    };

    const preloadCarouselImages = (index: number) => {
        if (images.length === 0) return;

        const indexesToPreload = [index, index + 1, index - 1].map(
            (value) => (value + images.length) % images.length
        );

        indexesToPreload.forEach((imageIndex) => {
            preloadImage(getGalleryDisplayImageUrl(images[imageIndex]));
        });
    };

    const openCarousel = (index: number) => {
        preloadCarouselImages(index);
        setActiveIndex(index);
        setViewMode('carousel');
        updateViewInUrl('carousel', { imageIndex: index, push: true });
    };

    const setCarouselIndex = (nextIndex: number) => {
        if (images.length === 0) return;
        const safeIndex = (nextIndex + images.length) % images.length;
        preloadCarouselImages(safeIndex);
        setActiveIndex(safeIndex);
        updateViewInUrl('carousel', { imageIndex: safeIndex });
    };

    const setViewModeFromUi = (nextView: 'grid' | 'list' | 'carousel') => {
        setViewMode(nextView);
        updateViewInUrl(nextView, { imageIndex: nextView === 'carousel' ? activeIndex : undefined });
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const requestedView = params.get('view');
        const requestedImage = params.get('image');

        if (requestedView === 'list') {
            if (viewMode !== 'list') setViewMode('list');
            return;
        }

        if (requestedView === 'carousel') {
            if (viewMode !== 'carousel') {
                setViewMode('carousel');
            }

            if (requestedImage !== null && images.length > 0) {
                const parsed = Number(requestedImage);
                const safeIndex = Number.isFinite(parsed)
                    ? Math.max(0, Math.min(images.length - 1, Math.floor(parsed)))
                    : 0;

                if (safeIndex !== activeIndex) {
                    setActiveIndex(safeIndex);
                }
            }

            return;
        }

        if (viewMode !== 'grid') {
            setViewMode('grid');
        }
    }, [location.search, images.length, activeIndex, viewMode]);

    useEffect(() => {
        if (images.length === 0) {
            setActiveIndex(0);
            return;
        }
        if (activeIndex >= images.length) {
            setActiveIndex(0);
        }
    }, [images.length, activeIndex]);

    useEffect(() => {
        if (viewMode !== 'carousel' || images.length === 0) return;

        preloadCarouselImages(activeIndex);
    }, [activeIndex, images, viewMode]);

    useEffect(() => {
        if (!infoPanelState || infoPanelState.type !== 'image') return;
        if (!images.some((image) => image.id === infoPanelState.imageId)) {
            setInfoPanelState(null);
        }
    }, [images, infoPanelState]);

    useEffect(() => {
        if (!gallery?.description || requiresPassword || infoPanelState) return;
        if (typeof window === 'undefined') return;
        if (!window.matchMedia('(min-width: 901px)').matches) return;

        const storageKey = `galleryInfoSeen:${galleryId}`;
        const hasSeenGalleryInfo = sessionStorage.getItem(storageKey);

        if (!hasSeenGalleryInfo) {
            const timer = window.setTimeout(() => {
                setInfoPanelState({ type: 'gallery' });
                sessionStorage.setItem(storageKey, 'true');
            }, 420);

            return () => {
                window.clearTimeout(timer);
            };
        }
    }, [gallery?.description, galleryId, infoPanelState, requiresPassword]);

    useEffect(() => {
        if (viewMode !== 'carousel' || !activeImage) return;
        if (infoPanelState?.type !== 'image') return;
        if (infoPanelState.imageId === activeImage.id) return;
        setInfoPanelState({ type: 'image', imageId: activeImage.id });
    }, [activeImage, activeInfoPanelImageId, infoPanelState?.type, viewMode]);

    useEffect(() => {
        if (!infoPanelState) return undefined;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setInfoPanelState(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [infoPanelState]);

    const renderInfoToggle = (image: GalleryType['images'][number], className = 'gallery-info-toggle') => {
        if (!hasImageDetails(image)) return null;

        return (
            <button
                type="button"
                className={className}
                aria-label={`Vis tekst for ${image.title || 'billedet'}`}
                aria-expanded={infoPanelState?.type === 'image' && infoPanelState.imageId === image.id}
                aria-controls="gallery-info-drawer"
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleInfoPanel(image.id);
                }}
            >
                <GalleryInfoIcon />
            </button>
        );
    };

    const renderDownloadToggle = (image: GalleryType['images'][number], className = 'gallery-download-toggle') => {
        if (!canDownload) return null;

        return (
            <button
                type="button"
                className={className}
                aria-label={`Download ${image.title || 'billedet'}`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleDownload(image.id);
                }}
            >
                <GalleryDownloadIcon />
            </button>
        );
    };

    const renderBuyToggle = (image: GalleryType['images'][number], className = 'gallery-buy-toggle') => {
        if (!getActivePrintOptions(image).length) return null;

        return (
            <button
                type="button"
                className={className}
                aria-label={`Tilføj print af ${image.title || 'billedet'} til kurv`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleBuyPrint(image);
                }}
            >
                <GalleryCartIcon />
            </button>
        );
    };

    const renderImageActions = (
        image: GalleryType['images'][number],
        infoClassName = 'gallery-info-toggle',
        downloadClassName = 'gallery-download-toggle',
        containerClassName = 'gallery-image-actions',
        buyClassName = 'gallery-buy-toggle'
    ) => {
        const infoToggle = renderInfoToggle(image, infoClassName);
        const downloadToggle = renderDownloadToggle(image, downloadClassName);
        const buyToggle = renderBuyToggle(image, buyClassName);

        if (!infoToggle && !downloadToggle && !buyToggle) return null;

        return (
            <div className={containerClassName}>
                {infoToggle}
                {downloadToggle}
                {buyToggle}
            </div>
        );
    };

    const renderGalleryBlock = (block: NonNullable<GalleryType['blocks']>[number]) => {
        const blockImages = (block.images || [])
            .map((blockImage) => images.find((image) => String(image.id) === String(blockImage.id)) || blockImage)
            .filter(Boolean);

        const openBlockImage = (imageId: string) => {
            const imageIndex = images.findIndex((image) => String(image.id) === String(imageId));
            if (imageIndex >= 0) {
                openCarousel(imageIndex);
            }
        };

        return (
            <section key={block.id} className="gallery-content-block">
                <div className="gallery-content-block__copy">
                    <p className="gallery-info-drawer__eyebrow">Galleri</p>
                    <h2>{block.title}</h2>
                    {block.description ? <p>{block.description}</p> : null}
                </div>
                {block.videoUrl ? (
                    <div className="gallery-content-block__video">
                        <video src={block.videoUrl} controls preload="metadata" />
                    </div>
                ) : null}
                {!!blockImages.length && (
                    <div className="gallery-content-block__images">
                        {blockImages.map((image) => (
                            <button
                                type="button"
                                key={`${block.id}-${image.id}`}
                                className="gallery-content-block__image"
                                onClick={() => openBlockImage(image.id)}
                            >
                                <img
                                    src={getGalleryDisplayImageUrl(image)}
                                    srcSet={buildGalleryResponsiveSrcSet(image)}
                                    sizes="(max-width: 768px) 44vw, 24vw"
                                    alt={image.title || block.title}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </section>
        );
    };

    if (loading) return <div>Indlæser...</div>;
    if (requiresPassword) {
        return (
            <div>
                <h2>Beskyttet galleri</h2>
                <p>Indtast den samme kode i brugernavn og adgangskode for at se galleriet.</p>
                <form
                    autoComplete="off"
                    onSubmit={async (event) => {
                        event.preventDefault();
                        if (username !== password) {
                            setError('Brugernavn og adgangskode skal være ens');
                            return;
                        }

                        setError(null);
                        try {
                            const data = await accessGalleryByPassword(galleryId, password);
                            const token = data.accessToken || null;
                            setAccessToken(token);
                            if (token) {
                                sessionStorage.setItem(`galleryAccessToken:${galleryId}`, token);
                            }
                            setRequiresPassword(false);
                        } catch (e) {
                            setError('Forkert adgangskode');
                        }
                    }}
                >
                    <input
                        type="text"
                        name="gallery-username"
                        autoComplete="off"
                        placeholder="Brugernavn"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                    <input
                        type="password"
                        name="gallery-password"
                        autoComplete="new-password"
                        placeholder="Adgangskode"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <button type="submit">Lås op</button>
                </form>
                {error && <div className="error">{error}</div>}
            </div>
        );
    }
    if (error) return <div>{error}</div>;

    return (
        <div className="gallery-page">
            {!!orderedGalleryBlocks.length && (
                <div className="gallery-content-blocks">
                    {orderedGalleryBlocks.map((block) => renderGalleryBlock(block))}
                </div>
            )}

            <header className={`gallery-header ${viewMode === 'carousel' ? 'gallery-header--carousel' : ''}`}>
                {location.search.includes('payment=success') && (
                    <div className="gallery-payment-notice">Tak for din bestilling. Du modtager en kvittering fra Stripe.</div>
                )}
                {location.search.includes('payment=cancelled') && (
                    <div className="gallery-payment-notice gallery-payment-notice--muted">Betalingen blev afbrudt.</div>
                )}
                {cartNotice && <div className="gallery-payment-notice">{cartNotice}</div>}
                <div className="gallery-view-toggle" role="tablist" aria-label="Visning">
                    {gallery?.description && (
                        <button
                            type="button"
                            className={`gallery-view-button gallery-view-button--icon ${infoPanelState?.type === 'gallery' ? 'is-active' : ''}`}
                            aria-pressed={infoPanelState?.type === 'gallery'}
                            aria-controls="gallery-info-drawer"
                            aria-label="Vis galleriinformation"
                            onClick={toggleGalleryInfoPanel}
                        >
                            <GalleryInfoIcon />
                        </button>
                    )}
                    <button
                        type="button"
                        className={`gallery-view-button ${viewMode === 'grid' ? 'is-active' : ''}`}
                        aria-pressed={viewMode === 'grid'}
                        onClick={() => setViewModeFromUi('grid')}
                    >
                        Miniature-matrix
                    </button>
                    <button
                        type="button"
                        className={`gallery-view-button ${viewMode === 'list' ? 'is-active' : ''}`}
                        aria-pressed={viewMode === 'list'}
                        onClick={() => setViewModeFromUi('list')}
                    >
                        Stor liste
                    </button>
                    <button
                        type="button"
                        className={`gallery-view-button ${viewMode === 'carousel' ? 'is-active' : ''}`}
                        aria-pressed={viewMode === 'carousel'}
                        onClick={() => setViewModeFromUi('carousel')}
                    >
                        Fullframe karrusel
                    </button>
                </div>
                {viewMode === 'carousel' && (
                    <span className="gallery-carousel__count gallery-carousel__count--header">
                        {images.length > 0 ? activeIndex + 1 : 0} / {images.length}
                    </span>
                )}
                <button
                    type="button"
                    className={`gallery-cart-button ${cartItemCount > 0 ? 'has-items' : ''}`}
                    onClick={() => setIsCartOpen(true)}
                    aria-label={`Åbn kurv med ${cartItemCount} print`}
                >
                    <GalleryCartIcon />
                    <span>{cartItemCount}</span>
                </button>
            </header>

            {viewMode === 'grid' && (
                <div className="gallery-grid gallery-grid--thumbs">
                    {images.map((image, index) => (
                        <article key={image.id} className="gallery-thumb-card">
                            <div className="gallery-image-head">
                                <p className="gallery-image-title">{image.title || 'Uden titel'}</p>
                                {renderImageActions(image)}
                            </div>
                            <button type="button" className="gallery-thumb" onClick={() => openCarousel(index)}>
                                <img src={image.thumbnailUrl} alt={image.title || gallery?.title || 'Billede'} />
                            </button>
                        </article>
                    ))}
                </div>
            )}

            {viewMode === 'list' && (
                <div className="gallery-list-view">
                    {images.map((image, index) => (
                        <article key={image.id} className="gallery-list-card">
                            <div className="gallery-image-head gallery-image-head--list">
                                <p className="gallery-image-title gallery-image-title--list-inline">{image.title || 'Uden titel'}</p>
                                {renderImageActions(
                                    image,
                                    'gallery-info-toggle',
                                    'gallery-download-toggle',
                                    'gallery-image-actions gallery-image-actions--list-inline'
                                )}
                            </div>
                            <button
                                type="button"
                                className="gallery-list-card__media"
                                onClick={() => openCarousel(index)}
                            >
                                <p className="gallery-image-title gallery-image-title--list-overlay">{image.title || 'Uden titel'}</p>
                                <img
                                    src={getGalleryDisplayImageUrl(image)}
                                    srcSet={buildGalleryResponsiveSrcSet(image)}
                                    sizes="(max-width: 768px) 92vw, 50vw"
                                    alt={image.title || gallery?.title || 'Billede'}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </button>
                        </article>
                    ))}
                    <button
                        type="button"
                        className="gallery-back-to-top"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                        Tilbage til toppen
                    </button>
                </div>
            )}

            {viewMode === 'carousel' && (
                <div className="gallery-carousel" aria-live="polite">
                    <div className="gallery-carousel__toolbar">
                        <div className="gallery-image-head gallery-image-head--carousel gallery-image-head--carousel-inline">
                            <p className="gallery-image-title gallery-image-title--carousel gallery-image-title--carousel-inline">
                                {activeImage?.title || 'Uden titel'}
                            </p>
                            {activeImage && renderImageActions(
                                activeImage,
                                'gallery-info-toggle gallery-info-toggle--carousel',
                                'gallery-download-toggle gallery-download-toggle--carousel',
                                'gallery-image-actions gallery-image-actions--carousel-inline'
                            )}
                        </div>
                    </div>
                    <div className="gallery-carousel__frame">
                        {activeImage && (
                            <div className="gallery-image-head gallery-image-head--carousel gallery-image-head--carousel-overlay">
                                <p className="gallery-image-title gallery-image-title--carousel gallery-image-title--carousel-overlay">
                                    {activeImage.title || 'Uden titel'}
                                </p>
                                {renderImageActions(
                                    activeImage,
                                    'gallery-info-toggle gallery-info-toggle--carousel',
                                    'gallery-download-toggle gallery-download-toggle--carousel',
                                    'gallery-image-actions gallery-image-actions--carousel-overlay'
                                )}
                            </div>
                        )}
                        {activeImage ? (
                            <img
                                src={getGalleryDisplayImageUrl(activeImage)}
                                srcSet={buildGalleryResponsiveSrcSet(activeImage)}
                                sizes="100vw"
                                alt={activeImage.title || gallery?.title || 'Billede'}
                                decoding="async"
                            />
                        ) : (
                            <div className="gallery-carousel__placeholder" />
                        )}
                        <button
                            type="button"
                            className="gallery-carousel__nav gallery-carousel__nav--prev"
                            onClick={() => setCarouselIndex(activeIndex - 1)}
                            aria-label="Forrige billede"
                            disabled={images.length === 0}
                        >
                            ‹
                        </button>
                        <button
                            type="button"
                            className="gallery-carousel__nav gallery-carousel__nav--next"
                            onClick={() => setCarouselIndex(activeIndex + 1)}
                            aria-label="Næste billede"
                            disabled={images.length === 0}
                        >
                            ›
                        </button>
                    </div>
                </div>
            )}

            {(infoPanelState?.type === 'gallery' || infoPanelImage) && (
                <div className="gallery-info-drawer is-open">
                    <button
                        type="button"
                        className="gallery-info-drawer__backdrop"
                        aria-label="Luk billedtekst"
                        onClick={handleInfoPanelCloseInteraction}
                    />
                    <aside
                        id="gallery-info-drawer"
                        className="gallery-info-drawer__panel"
                        aria-label="Billedtekst"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="gallery-info-drawer__header">
                            <div>
                                <p className="gallery-info-drawer__eyebrow">
                                    {infoPanelState?.type === 'gallery' ? 'Galleri' : 'Billedtekst'}
                                </p>
                                <h2>
                                    {infoPanelState?.type === 'gallery'
                                        ? gallery?.title || 'Galleri'
                                        : infoPanelImage?.title || 'Uden titel'}
                                </h2>
                            </div>
                            <button
                                type="button"
                                className="gallery-info-drawer__close"
                                onClick={handleInfoPanelCloseInteraction}
                            >
                                Luk
                            </button>
                        </div>
                        {infoPanelState?.type === 'gallery' && gallery?.description && (
                            <p className="gallery-info-drawer__description">{gallery.description}</p>
                        )}
                        {infoPanelState?.type === 'image' && infoPanelImage?.description && (
                            <p className="gallery-info-drawer__description">{infoPanelImage.description}</p>
                        )}
                        {infoPanelState?.type === 'image' && infoPanelImage && getDreamyText(infoPanelImage) && (
                            <div className="gallery-dreamy gallery-dreamy--drawer">
                                <p className="gallery-dreamy__text">
                                    {expandedDreamy[infoPanelImage.id]
                                        ? getDreamyText(infoPanelImage)
                                        : getDreamyPreview(getDreamyText(infoPanelImage))}
                                </p>
                                {shouldShowDreamyToggle(getDreamyText(infoPanelImage)) && (
                                    <button
                                        type="button"
                                        className="gallery-dreamy__toggle"
                                        onClick={() =>
                                            setExpandedDreamy((prev) => ({
                                                ...prev,
                                                [infoPanelImage.id]: !prev[infoPanelImage.id],
                                            }))
                                        }
                                    >
                                        {expandedDreamy[infoPanelImage.id] ? 'Skjul' : 'Læs mere'}
                                    </button>
                                )}
                            </div>
                        )}
                    </aside>
                </div>
            )}

            {purchaseImage && (
                <div className="gallery-purchase-modal is-open">
                    <button
                        type="button"
                        className="gallery-info-drawer__backdrop"
                        aria-label="Luk køb"
                        onClick={closePurchaseDialog}
                    />
                    <section className="gallery-purchase-modal__panel" aria-label="Køb print">
                        <div className="gallery-info-drawer__header">
                            <div>
                                <p className="gallery-info-drawer__eyebrow">Print</p>
                                <h2>{purchaseImage.title || 'Uden titel'}</h2>
                            </div>
                            <button type="button" className="gallery-info-drawer__close" onClick={closePurchaseDialog} disabled={purchasing}>
                                Luk
                            </button>
                        </div>
                        <div className="gallery-print-options" role="radiogroup" aria-label="Vælg printstørrelse">
                            {getActivePrintOptions(purchaseImage).map((option) => (
                                <label key={option.id} className="gallery-print-option">
                                    <input
                                        type="radio"
                                        name="printOption"
                                        checked={selectedPrintOptionId === option.id}
                                        onChange={() => setSelectedPrintOptionId(option.id || null)}
                                    />
                                    <span>{option.label}</span>
                                    <strong>{formatPrintPrice(option)}</strong>
                                </label>
                            ))}
                        </div>
                        {purchaseError && <div className="error">{purchaseError}</div>}
                        <div className="modal-actions">
                            <button type="button" className="link-button" onClick={closePurchaseDialog} disabled={purchasing}>
                                Annuller
                            </button>
                            <button type="button" onClick={confirmSelectedPurchase} disabled={purchasing}>
                                Tilføj til kurv
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {isCartOpen && (
                <div className="gallery-purchase-modal is-open">
                    <button
                        type="button"
                        className="gallery-info-drawer__backdrop"
                        aria-label="Luk kurv"
                        onClick={() => !purchasing && setIsCartOpen(false)}
                    />
                    <section className="gallery-purchase-modal__panel gallery-cart-panel" aria-label="Kurv">
                        <div className="gallery-info-drawer__header">
                            <div>
                                <p className="gallery-info-drawer__eyebrow">Kurv</p>
                                <h2>{cartItemCount ? `${cartItemCount} print` : 'Kurven er tom'}</h2>
                            </div>
                            <button
                                type="button"
                                className="gallery-info-drawer__close"
                                onClick={() => setIsCartOpen(false)}
                                disabled={purchasing}
                            >
                                Luk
                            </button>
                        </div>

                        {!cartItems.length ? (
                            <p className="gallery-list-meta">Tilføj print fra galleriet før betaling.</p>
                        ) : (
                            <div className="gallery-cart-items">
                                {cartItems.map((item) => (
                                    <div key={item.key} className="gallery-cart-item">
                                        {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt={item.imageTitle} /> : null}
                                        <div className="gallery-cart-item__body">
                                            <strong>{item.imageTitle}</strong>
                                            <span>{item.printLabel}</span>
                                            <span>{formatCartAmount(item.priceCents, item.currency)} pr. stk.</span>
                                        </div>
                                        <div className="gallery-cart-item__controls">
                                            <label>
                                                <span>Antal</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="20"
                                                    value={item.quantity}
                                                    onChange={(event) => updateCartQuantity(item.key, Number(event.target.value) || 1)}
                                                />
                                            </label>
                                            <strong>{formatCartAmount(item.priceCents * item.quantity, item.currency)}</strong>
                                            <button type="button" className="link-button link-button--danger" onClick={() => removeCartItem(item.key)}>
                                                Fjern
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {purchaseError && <div className="error">{purchaseError}</div>}
                        <div className="gallery-cart-total">
                            <span>Total</span>
                            <strong>{formatCartAmount(cartTotalCents, cartCurrency)}</strong>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="link-button" onClick={() => setIsCartOpen(false)} disabled={purchasing}>
                                Fortsæt med at vælge
                            </button>
                            <button type="button" onClick={checkoutCart} disabled={!cartItems.length || purchasing}>
                                {purchasing ? 'Sender videre...' : 'Gå til betaling'}
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default Gallery;